import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "@/contexts/AuthContext";
import type { birthdays_BirthdayEntry } from "@/api/models/birthdays_BirthdayEntry";
import type { birthdays_UpcomingBirthdaysResponse } from "@/api/models/birthdays_UpcomingBirthdaysResponse";
import type { birthdays_ParentBirthdayEntry } from "@/api/models/birthdays_ParentBirthdayEntry";

export interface BirthdayEntry {
  id: string;
  firstname: string;
  lastname: string;
  date: string;
  age: number;
  is_today: boolean;
}

export type ParentBirthdayEntry = birthdays_ParentBirthdayEntry;

export interface UpcomingBirthdaysResponse {
  children: BirthdayEntry[];
  parents: ParentBirthdayEntry[];
  employees: BirthdayEntry[];
}

export const upcomingBirthdaysKeys = {
  all: ["birthdays", "upcoming"] as const,
  list: (orgId: string | null | undefined, days: number) =>
    [...upcomingBirthdaysKeys.all, { orgId: orgId ?? null, days }] as const,
};

/** The generated DTO marks every field optional; the server always sends them. */
function toEntry(entry: birthdays_BirthdayEntry): BirthdayEntry {
  return {
    id: entry.id ?? "",
    firstname: entry.firstname ?? "",
    lastname: entry.lastname ?? "",
    date: entry.date ?? "",
    age: entry.age ?? 0,
    is_today: entry.is_today ?? false,
  };
}

/** The route's answer with every entry filled in. */
export function toUpcomingBirthdays(response: birthdays_UpcomingBirthdaysResponse): UpcomingBirthdaysResponse {
  return {
    children: (response.children ?? []).map(toEntry),
    parents: response.parents ?? [],
    employees: (response.employees ?? []).map(toEntry),
  };
}

/**
 * Children, parents and employees with a birthday today or in the next `days`
 * days, for the dashboard's Upcoming card.
 *
 * Reads the staff-level `GET /api/v1/birthdays/upcoming`, the route the
 * employee app uses: the same computation as the owner-only cockpit route,
 * plus the children's parents. Organisation-wide (no `childcare_id`).
 */
export function useUpcomingBirthdays(days = 14, options?: { enabled?: boolean }) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: upcomingBirthdaysKeys.list(orgId, days),
    queryFn: async (): Promise<UpcomingBirthdaysResponse> => {
      const { BirthdaysService } = await import("@/api");
      return toUpcomingBirthdays(await BirthdaysService.getApiV1BirthdaysUpcoming(days));
    },
    staleTime: 5 * 60 * 1000,
    enabled: (options?.enabled ?? true) && Boolean(isSignedIn) && isAuthReady && !!orgId,
  });
}
